import { Fragment, useEffect, useRef, useState } from 'react'
import { countdown, mwh, price, qty } from '../format'
import { useScrollMemory } from '../useScrollMemory'
import type { Contract, MarketPoint, OwnStats, Ticker } from '../types'

/**
 * Kontrat listesi.
 *
 *              │     │       Alış        │      Satış       │
 *   Kontrat    │ PTF │ B.F │ Hacim │Fiyat│Fiyat│Hacim│ B.F  │ NetP
 *
 * `B.F`  — kendi teklif fiyatımız: o kontrattaki AÇIK gölge emirlerimizin en
 *          iyisi (alışta en yüksek, satışta en düşük). Renklendirme normal
 *          fiyatla aynı: PTF'ye göre.
 * `NetP` — o kontrattaki net pozisyonumuz; eşleşmelerden hesaplanıyor ve
 *          kaynağı kalıcı kayıt (Postgres). Pozitif yeşil, negatif kırmızı.
 *          **MWh cinsinden gösterilir** (1 lot = 0,1 MWh); sistem içeride
 *          lot tutuyor, çeviri yalnız burada.
 * `Hacim`/`Fiyat` — en iyi alıcının / satıcının miktarı ve fiyatı.
 *
 * Fiyatlar PTF'ye göre renklendirilmiş kutucuklarda: alışta PTF'nin altı,
 * satışta PTF'nin üstü lehimize (yeşil); tersi aleyhimize (kırmızı); eşitse
 * turuncu. Aynı kural B.F sütunlarına da uygulanır.
 *
 * Kontrat adının son iki hanesi (teslim saati) kalın; adın altındaki ince çizgi
 * kapanışa kalan süreyi gösterir — yalnız kapanışa en yakın kontratta.
 */

interface Props {
  contracts: Contract[]
  board: Record<string, Ticker>
  /** SmartPulse verisi — sistem yönü ve SMF. */
  marketData: Record<string, MarketPoint>
  selected: string | null
  now: number
  onSelect: (contract: string) => void
  /** Kontrat başına kendi tekliflerimiz ve net pozisyonumuz. */
  own: Record<string, OwnStats>
  /**
   * Telefon düzeni: on sütunluk tablo yerine iki satırlık kart. Sütun başlığı
   * da render edilmez — kartta her değerin kendi etiketi var.
   */
  mobil?: boolean
}

const HOUR = 3_600_000
const TWENTY_MINUTES = 1_200_000
const FIVE_MINUTES = 300_000

/** Boş sütunlar (B.F, NetP) — kırık görünmesin diye yer tutucu. */
const BLANK = '–'

/** Açılışta gösterilen kapalı kontrat sayısı (en yeniden geriye). */
const CLOSED_AT_FIRST = 7
/** Butona her basışta eskiye doğru kaç kontrat daha açılır. */
const CLOSED_STEP = 8

export function ContractList({
  own,
  contracts,
  board,
  marketData,
  selected,
  now,
  onSelect,
  mobil = false,
}: Props) {
  // Kapalı kontratlar kademeli açılıyor: açıklar her zaman tam, kapalılardan
  // başlangıçta en yeni 7 tanesi. Sunucu arşivin tamamını gönderdiği için bu
  // bir "daha fazla getir" isteği değil, elimizdekinden daha fazlasını gösterme.
  const [shownClosed, setShownClosed] = useState(CLOSED_AT_FIRST)
  const [notice, setNotice] = useState(false)
  // Kaydırma konumu bileşen sökülse de yaşar: mobilde bir kontrata dokunup
  // İşlem ekranına gitmek bu sayfayı söküyor, geri dönüşte liste en üstten
  // başlıyordu. Anahtar düzene göre ayrı — mobil kart ile masaüstü satırının
  // yükseklikleri farklı, pencere yeniden boyutlanınca konum anlamsız olurdu.
  const rowsRef = useScrollMemory<HTMLDivElement>(`contracts:${mobil ? 'm' : 'd'}`)

  useEffect(() => {
    if (!notice) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNotice(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [notice])

  if (contracts.length === 0) {
    return <div className="panel empty">Tahta bekleniyor…</div>
  }

  // Liste eskiden yeniye sıralı → kapalılar başta, en yenileri sonda.
  const closed = contracts.filter((c) => c.closed)
  const open = contracts.filter((c) => !c.closed)
  const visible = [...closed.slice(Math.max(0, closed.length - shownClosed)), ...open]
  const hasOlder = shownClosed < closed.length
  // Kapalı/açık sınırı: ilk açık kontratın ÜSTÜNE kırmızı ayraç çizgisi
  // çekilir ("buradan öncesi kapalı") — yalnız iki grup da görünürken.
  const firstOpenKey = open[0]?.key ?? null
  const closedVisible = visible.some((c) => c.closed)

  const loadOlder = () => {
    if (!hasOlder) {
      setNotice(true)
      return
    }
    setShownClosed((n) => n + CLOSED_STEP)
  }

  // Sayaç çizgisi YALNIZ kapanışa en yakın AÇIK kontratta. Diğerlerinde
  // göstermek yanıltıcıydı: saatler sonra kapanacak kontratlar da "sayaç
  // işliyor" gibi duruyordu. Liste kapanmışlarla başladığı için `closed`
  // olanlar ayrıca eleniyor.
  const nearestKey =
    contracts.find((c) => {
      if (c.closed) return false
      const left = c.closesAt ? new Date(c.closesAt).getTime() - now : null
      return left != null && left > 0
    })?.key ?? null

  return (
    <div className="panel">
      <div className="panel-title">
        <span className="title-group">
          Kontratlar
          {/* Eski kontratlar listenin ÜSTÜNE ekleniyor — ok bu yüzden yukarı */}
          <button
            type="button"
            className="icon-btn"
            onClick={loadOlder}
            title={`Daha eski ${CLOSED_STEP} kontratı yükle`}
            aria-label="Daha eski kontratları yükle"
          >
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <path d="M3.5 9.5 8 5l4.5 4.5" />
              <path d="M3.5 13 8 8.5l4.5 4.5" />
            </svg>
          </button>
        </span>
      </div>

      {/* Başlık kaydırma kabının İÇİNDE: dışarıdayken kap kaydırma çubuğu
          kadar daralıyor ve sütun ayırıcıları başlıktan sonra kayıyordu.
          `position: sticky` ile yine hep görünür kalıyor. */}
      <div ref={rowsRef} className={`contract-rows${mobil ? ' mobil' : ''}`}>
        {!mobil && (
        <div className="contract-head">
        <span className="group-head" />
        <span className="group-head" />
        <span className="group-head" />
        <span className="group-head group-bid">Alış</span>
        <span className="group-head group-ask">Satış</span>
        <span className="group-head" />

        <span className="col-name">Kontrat</span>
        <span>PTF</span>
        <span>Yön</span>
        <span>B.F</span>
        <span>Hacim</span>
        <span>Fiyat</span>
        <span>Fiyat</span>
        <span>Hacim</span>
        <span>B.F</span>
          <span>NetP</span>
        </div>
        )}

        {visible.map((contract) => {
          const ticker = board[contract.key]
          const remaining = contract.closesAt ? new Date(contract.closesAt).getTime() - now : null

          return (
            <Fragment key={contract.key}>
              {/* Kapalı/açık sınır çizgisi — "buradan öncesi kapalı". */}
              {closedVisible && contract.key === firstOpenKey && (
                <div
                  className="closed-divider"
                  role="separator"
                  title="Bu çizginin üstündeki kontratlar kapandı"
                />
              )}
              {mobil ? (
                <MobilSatir
                  contract={contract}
                  ticker={ticker}
                  point={marketData[contract.key]}
                  own={own[contract.key]}
                  secili={contract.key === selected}
                  remaining={contract.key === nearestKey ? remaining : null}
                  onSelect={onSelect}
                />
              ) : (
              <button
                className={`contract-row${contract.key === selected ? ' selected' : ''}`}
                onClick={() => onSelect(contract.key)}
                aria-pressed={contract.key === selected}
              >
                <span className="contract-name">
                  <span className="contract-line">
                    {/* Yeşil nokta = işlem görüyor, gri = kapanmış (verisi arşivden) */}
                    <span
                      className={`status-dot${contract.closed ? ' closed' : ''}`}
                      aria-hidden="true"
                    />
                    <span className="contract-key">
                      {contract.key.slice(0, -2)}
                      <b>{contract.key.slice(-2)}</b>
                    </span>
                  </span>
                  <Lifebar remaining={contract.key === nearestKey ? remaining : null} />
                </span>

                <span className="num">{price(ticker?.mcp)}</span>
                <SystemDirection point={marketData[contract.key]} />

                {contract.closed ? (
                  // Kapalı kontratta tahta yok: alış/satış hücreleri BOŞ —
                  // yer tutucu bile gürültü (kullanıcı isteği 2026-07-29).
                  <>
                    <span /><span /><span /><span /><span /><span />
                  </>
                ) : (
                  <>
                    {/* Alış: B.F · Hacim · Fiyat */}
                    <span className="num">
                      <PriceChip value={own[contract.key]?.bid ?? null} mcp={ticker?.mcp} side="bid" />
                    </span>
                    <span className="num">{qty(ticker?.bestBidQuantity)}</span>
                    <span className="num">
                      <PriceChip value={ticker?.bestBidPrice} mcp={ticker?.mcp} side="bid" />
                    </span>

                    {/* Satış: Fiyat · Hacim · B.F */}
                    <span className="num">
                      <PriceChip value={ticker?.bestAskPrice} mcp={ticker?.mcp} side="ask" />
                    </span>
                    <span className="num">{qty(ticker?.bestAskQuantity)}</span>
                    <span className="num">
                      <PriceChip value={own[contract.key]?.ask ?? null} mcp={ticker?.mcp} side="ask" />
                    </span>
                  </>
                )}

                <span className="num">
                  <NetChip value={own[contract.key]?.net ?? 0} plain={contract.closed} />
                </span>
              </button>
              )}
            </Fragment>
          )
        })}
      </div>

      {notice && (
        <div className="modal-backdrop" onClick={() => setNotice(false)}>
          <div
            className="modal"
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <p>Daha eski kontrat verisi bulunamadı.</p>
            <button type="button" className="modal-ok" onClick={() => setNotice(false)} autoFocus>
              Tamam
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Telefon kartı — iki satır:
 *
 *   ● PT26080219 ◆            PTF 2.412,0 ▲12
 *   ─────── kapanış çizgisi ───────
 *   A 2.410,0 ×15   S 2.415,5 ×8        Net +0,5
 *
 * On sütunluk tablo telefonda okunmuyordu; burada yalnız karar için gereken
 * beş değer var. `B.F` (kendi en iyi teklifimiz) sayı olarak DEĞİL, adın
 * yanında mor bir işaretle duruyor — "bu kontratta açık teklifin var" demek
 * yeter, fiyatı İşlem ekranında zaten görülüyor. Satırın tamamı dokunma
 * hedefi ve dokunmak İşlem ekranına götürüyor.
 */
function MobilSatir({
  contract,
  ticker,
  point,
  own,
  secili,
  remaining,
  onSelect,
}: {
  contract: Contract
  ticker: Ticker | undefined
  point: MarketPoint | undefined
  own: OwnStats | undefined
  secili: boolean
  remaining: number | null
  onSelect: (contract: string) => void
}) {
  const teklifimizVar = own?.bid != null || own?.ask != null

  return (
    <button
      className={`contract-row mobil${secili ? ' selected' : ''}`}
      onClick={() => onSelect(contract.key)}
      aria-pressed={secili}
    >
      <span className="mk-ust">
        <span className="mk-ad">
          <span className={`status-dot${contract.closed ? ' closed' : ''}`} aria-hidden="true" />
          <span className="contract-key">
            {contract.key.slice(0, -2)}
            <b>{contract.key.slice(-2)}</b>
          </span>
          {teklifimizVar && (
            <span className="mk-benim" title="Bu kontratta açık teklifin var" aria-hidden="true" />
          )}
        </span>

        <span className="mk-ptf">
          <span className="mk-etiket">PTF</span>
          <span className="num">{price(ticker?.mcp)}</span>
          <SystemDirection point={point} />
        </span>
      </span>

      <Lifebar remaining={remaining} />

      <span className="mk-alt">
        {contract.closed ? (
          <span className="mk-kapali">kapandı</span>
        ) : (
          <>
            <span className="mk-yan">
              <span className="mk-etiket">A</span>
              <PriceChip value={ticker?.bestBidPrice} mcp={ticker?.mcp} side="bid" />
              <span className="num mk-lot">{qty(ticker?.bestBidQuantity)}</span>
            </span>
            <span className="mk-yan">
              <span className="mk-etiket">S</span>
              <PriceChip value={ticker?.bestAskPrice} mcp={ticker?.mcp} side="ask" />
              <span className="num mk-lot">{qty(ticker?.bestAskQuantity)}</span>
            </span>
          </>
        )}
        <span className="mk-net">
          <NetChip value={own?.net ?? 0} plain={contract.closed} />
        </span>
      </span>
    </button>
  )
}

/**
 * Sistem yönü (SmartPulse). Pozitif = enerji açığı → kırmızı, yukarı ok;
 * negatif = enerji fazlası → yeşil, aşağı ok.
 *
 * Üzerine gelince SMF gösterilir. SMF sonradan kesinleşiyor — henüz
 * belirlenmemiş saatlerde "–" yazar (kaynak orada gerçek sıfır değil, yer
 * tutucu döndürüyor; bkz. `smartpulse.py`).
 */
function SystemDirection({ point }: { point: MarketPoint | undefined }) {
  const value = point?.systemDirection ?? null
  const smf = point?.smp != null ? price(point.smp) : BLANK
  const title = `SMF: ${smf}`

  if (value == null) {
    return (
      <span className="num placeholder" title={title}>
        {BLANK}
      </span>
    )
  }

  const acik = value > 0
  return (
    <span className={`num sysdir ${acik ? 'up' : 'down'}`} title={title}>
      {qty(value)}
      <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
        {acik ? <path d="M5 8.5V2M5 2 1.8 5.2M5 2l3.2 3.2" /> : <path d="M5 1.5V8M5 8l3.2-3.2M5 8 1.8 4.8" />}
      </svg>
      <span className="sr-only">{acik ? 'enerji açığı' : 'enerji fazlası'}</span>
    </span>
  )
}

/**
 * PTF'ye göre renklendirilmiş fiyat kutucuğu.
 *
 *   alış  < PTF → yeşil (ucuza alıyoruz)   |  satış < PTF → kırmızı
 *   alış  > PTF → kırmızı                  |  satış > PTF → yeşil
 *   alış == PTF → turuncu                  |  satış == PTF → turuncu
 *
 * PTF henüz yoksa nötr kutucuk; fiyat yoksa yer tutucu.
 *
 * Not: renk tek başına bilgi taşımıyor — fiyat ve PTF sayıları aynı satırda
 * duruyor, karşılaştırma gözle de yapılabiliyor.
 */
function PriceChip({
  value,
  mcp,
  side,
}: {
  value: number | null | undefined
  mcp: number | null | undefined
  side: 'bid' | 'ask'
}) {
  // Kanca koşulsuz çağrılmalı — `value == null` erken dönüşü BUNDAN SONRA.
  const flash = usePriceFlash(value)

  if (value == null) return <span className="placeholder">{BLANK}</span>

  let tone = 'neutral'
  if (mcp != null) {
    if (value === mcp) tone = 'even'
    else {
      // Alışta PTF'nin altı, satışta üstü lehimize.
      const ucuz = value < mcp
      tone = (side === 'bid' ? ucuz : !ucuz) ? 'good' : 'bad'
    }
  }

  return (
    <span className={`price-chip ${tone}${flash ? ` flash-${flash}` : ''}`}>
      {/* Değer ayrı katmanda: parlama örtüsü yazının ÜSTÜNE binmesin. */}
      <span className="chip-value">{price(value)}</span>
    </span>
  )
}

/** Parlama süresi (ms). Fiyatlar saniyede bir güncellendiği için 1 sn'nin altında. */
const FLASH_MS = 600

/**
 * Fiyat değiştiğinde kısa bir parlama yönü döndürür: `up` | `down` | null.
 *
 * Yön **fiyatın kendi hareketi**, PTF karşılaştırması değil — kutucuğun kalıcı
 * rengi (yeşil/kırmızı) PTF'ye göre, parlama ise son değişime göre.
 *
 * İlk render'da parlamaz: `previous` başlangıçta güncel değerle doluyor. Aksi
 * hâlde sayfa açılışında ve kontrat listesi her tazelendiğinde bütün kutucuklar
 * birden yanıp sönerdi.
 *
 * `null` değerler zinciri kırıyor (veri gelmediğinde parlamıyoruz): tahtada
 * fiyat bir tur boş kalıp geri geldiğinde bu bir "değişim" değil.
 */
function usePriceFlash(value: number | null | undefined): 'up' | 'down' | null {
  const previous = useRef(value)
  const [flash, setFlash] = useState<'up' | 'down' | null>(null)

  useEffect(() => {
    const onceki = previous.current
    previous.current = value
    if (onceki == null || value == null || value === onceki) return

    setFlash(value > onceki ? 'up' : 'down')
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS)
    return () => window.clearTimeout(timer)
  }, [value])

  return flash
}

/**
 * Net pozisyon kutucuğu. Pozitif (net alıcıyız) yeşil, negatif (net satıcı)
 * kırmızı, sıfırsa yer tutucu.
 *
 * `plain` (kapalı kontrat): pozisyon hâlâ bilgi ama artık eylem konusu değil —
 * renkli kutu yerine sade yazı (kullanıcı isteği 2026-07-29).
 *
 * `PriceChip`ten ayrı bileşen: oradaki renk PTF karşılaştırmasına dayanıyor,
 * burada ölçüt işaretin kendisi. Aynı kutucuk görünümünü paylaşıyorlar ama
 * mantıkları farklı — tek bileşene sıkıştırmak ikisini de bulanıklaştırırdı.
 *
 * Değer sunucuda **kalıcı kayıttan** hesaplanıyor (kullanıcı isteği), yani
 * bellekteki dolum listesinden değil.
 */
function NetChip({ value, plain = false }: { value: number; plain?: boolean }) {
  // Tam sıfır "pozisyon yok" demek; kutucuk/yazı göstermek gürültü olurdu.
  if (!value) return <span className="placeholder">{BLANK}</span>
  const isaret = value > 0 ? '+' : ''
  // Tooltip'te lot da var: sistem içeride lot tutuyor, ekranda MWh
  // gösteriliyor — ikisi arasında köprü kalsın.
  const ipucu = `Net pozisyon: ${isaret}${mwh(value)} MWh (${isaret}${qty(value)} lot)`

  if (plain) {
    return (
      <span className="net-plain" title={ipucu}>
        {isaret}
        {mwh(value)}
      </span>
    )
  }
  return (
    <span
      // `net`: fiyat kutusundan bilinçli küçük sabit boy (bkz. styles.css).
      className={`price-chip net ${value > 0 ? 'good' : 'bad'}`}
      title={ipucu}
    >
      <span className="chip-value">
        {isaret}
        {mwh(value)}
      </span>
    </span>
  )
}

/**
 * Kapanışa kalan süre — kısalan ince çizgi.
 *
 * Yalnız kapanışa en yakın kontratta çizilir; saatlik kontratlar teslimden bir
 * saat önce kapandığı için o kontratın kalan süresi **daima 1 saatin altındadır**.
 * Ölçek bu yüzden sabit 1 saat: çizgi bir saatten sıfıra doğru erir.
 *
 * Yeşil → son 20 dakikada turuncu → son 5 dakikada kırmızı.
 *
 * `remaining` null ise görünmez bir yer tutucu döner — satır yükseklikleri eşit
 * kalsın, çizgisiz satırlar kısalıp liste zıplamasın.
 */
function Lifebar({ remaining }: { remaining: number | null }) {
  if (remaining == null || remaining <= 0) return <span className="lifebar" />

  const tone =
    remaining <= FIVE_MINUTES ? 'critical' : remaining <= TWENTY_MINUTES ? 'soon' : 'calm'
  // %1 taban: son saniyelere kadar çizgi tamamen kaybolmasın, renk uyarısı dursun.
  const width = Math.max(1, Math.min(100, (remaining / HOUR) * 100))

  return (
    <span className="lifebar active" title={`Kapanışa ${countdown(remaining)}`}>
      <span className={`lifebar-fill ${tone}`} style={{ width: `${width}%` }} />
    </span>
  )
}
